import { useEffect, useState, type InputHTMLAttributes } from 'react';
import { useForm, type UseFormRegisterReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';
import type { Renewal } from '@bekebi/contracts/api';
import { MONEY_PATTERN, RENEWAL_CURRENCY, isValidIsoDate } from '@bekebi/domain';
import { Button } from '../../components/button';
import { ErrorState, LoadingState } from '../../components/states';
import { useAllResources } from '../infrastructure/api';
import { useServices } from '../services/api';
import { formatDate } from '../services/format';
import { useOpenRenewal, useRenewTerm, useUpcomingRenewals, useUpdateRenewal } from './api';

/**
 * Renewal forms — exactly the fields the API accepts: term start, renewal date and price (USD,
 * two decimals). Dates are the browser's date inputs (YYYY-MM-DD, no time or timezone). The API
 * validates everything again and has the final word.
 */

const dateField = (label: string) =>
  z.string().refine(isValidIsoDate, { message: `Enter the ${label} as a valid date.` });
const priceField = z
  .string()
  .trim()
  .regex(MONEY_PATTERN, 'Enter an amount with two decimals, for example 120.00.');
const ORDER_MESSAGE = 'The renewal date must be after the term start.';

function Field({
  id,
  label,
  hint,
  error,
  inputProps,
}: {
  id: string;
  label: string;
  hint?: string;
  error: string | undefined;
  inputProps: InputHTMLAttributes<HTMLInputElement> & UseFormRegisterReturn;
}) {
  const describedBy = [hint ? `${id}-hint` : null, error ? `${id}-error` : null]
    .filter(Boolean)
    .join(' ');
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-fg-2">
        {label}
      </label>
      <input
        id={id}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={describedBy || undefined}
        className="mt-1 block w-full max-w-xs rounded-md border border-line-strong bg-surface px-3 py-2 text-sm"
        {...inputProps}
      />
      {hint ? (
        <p id={`${id}-hint`} className="mt-1 text-xs text-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={`${id}-error`} className="mt-1 text-sm text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

const PRICE_HINT = `${RENEWAL_CURRENCY}, two decimals (for example 120.00).`;

// ---------------------------------------------------------------------------------------------
// Open a renewal term (/app/renewals/new) — POST /api/services/:id/renewals, or for an
// infrastructure entry (the VPS, the domain) POST /api/infrastructure/resources/:id/renewals
// ---------------------------------------------------------------------------------------------

const openSchema = z
  .object({
    /** "service:<id>" or "resource:<id>". */
    subject: z.string().min(1, 'Choose what this term renews.'),
    termStart: dateField('term start'),
    renewalDate: dateField('renewal date'),
    price: priceField,
  })
  .refine(
    (v) =>
      !isValidIsoDate(v.termStart) || !isValidIsoDate(v.renewalDate) || v.termStart < v.renewalDate,
    {
      path: ['renewalDate'],
      message: ORDER_MESSAGE,
    },
  );
type OpenFields = z.infer<typeof openSchema>;

export function OpenRenewalPage() {
  const services = useServices();
  // Infrastructure entries (Admin: the form needs renewals.manage, which comes with
  // infrastructure.view) — the VPS and the domain renew too.
  const resources = useAllResources(true);
  const openResourceTerms = useUpcomingRenewals(null);
  const open = useOpenRenewal();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OpenFields>({
    resolver: zodResolver(openSchema),
    defaultValues: {
      subject: params.get('service')
        ? `service:${params.get('service')}`
        : params.get('resource')
          ? `resource:${params.get('resource')}`
          : '',
      termStart: '',
      renewalDate: '',
      price: '',
    },
  });

  const onSubmit = handleSubmit((v) =>
    open.mutate(v, {
      onSuccess: (renewal) =>
        void navigate(`/app/renewals/${renewal.id}`, {
          state: { notice: 'The renewal term was opened.' },
        }),
    }),
  );

  const resourcesWithOpenTerm = new Set(
    (openResourceTerms.data?.pages ?? []).flatMap((p) =>
      p.items.flatMap((t) => (t.resourceId !== null ? [t.resourceId] : [])),
    ),
  );

  if (services.isPending) return <LoadingState label="Loading services…" />;
  if (services.isError) {
    return (
      <ErrorState
        title="Services could not be loaded"
        message={services.error.message}
        onRetry={() => void services.refetch()}
      />
    );
  }

  return (
    <div className="max-w-xl">
      <p className="text-sm">
        <Link
          to="/app/renewals"
          className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
        >
          Renewals
        </Link>
      </p>
      <h1 className="mt-2">Open a renewal term</h1>
      <p className="mt-1 text-sm text-muted">
        A service or infrastructure entry can have one open renewal term at a time. When a term is
        due, renew it from its page.
      </p>
      <form
        onSubmit={onSubmit}
        noValidate
        aria-label="Open a renewal term"
        className="mt-6 space-y-5"
      >
        <div>
          <label htmlFor="renewal-service" className="block text-sm font-medium text-fg-2">
            Renews
          </label>
          <select
            id="renewal-service"
            className="mt-1 block min-h-10 w-full rounded-md border border-line-strong bg-surface px-3 text-sm"
            {...register('subject')}
          >
            <option value="">Choose a service or infrastructure entry…</option>
            <optgroup label="Services">
              {services.data.map((s) => (
                <option key={s.id} value={`service:${s.id}`} disabled={s.renewal !== null}>
                  {s.name}
                  {s.type === 'website' && s.accessUrl ? ` (${s.accessUrl})` : ''}
                  {s.renewal !== null ? ' — already has an open term' : ''}
                </option>
              ))}
            </optgroup>
            {resources.data && resources.data.length > 0 ? (
              <optgroup label="Infrastructure">
                {resources.data.map((r) => {
                  const busy = resourcesWithOpenTerm.has(r.id);
                  return (
                    <option key={r.id} value={`resource:${r.id}`} disabled={busy}>
                      {r.name}
                      {busy ? ' — already has an open term' : ''}
                    </option>
                  );
                })}
              </optgroup>
            ) : null}
          </select>
          {errors.subject ? (
            <p className="mt-1 text-sm text-danger">{errors.subject.message}</p>
          ) : null}
        </div>
        <Field
          id="renewal-term-start"
          label="Term start"
          error={errors.termStart?.message}
          inputProps={{ ...register('termStart'), type: 'date' }}
        />
        <Field
          id="renewal-date"
          label="Renewal date"
          error={errors.renewalDate?.message}
          inputProps={{ ...register('renewalDate'), type: 'date' }}
        />
        <Field
          id="renewal-price"
          label="Price"
          hint={PRICE_HINT}
          error={errors.price?.message}
          inputProps={{ ...register('price'), inputMode: 'decimal', autoComplete: 'off' }}
        />
        {open.isError ? (
          <p role="alert" className="text-sm text-danger">
            {open.error.message}
          </p>
        ) : null}
        <div className="flex gap-3">
          <Button type="submit" disabled={open.isPending}>
            {open.isPending ? 'Opening…' : 'Open renewal term'}
          </Button>
          <Link
            to="/app/renewals"
            className="inline-flex min-h-10 items-center rounded-md border border-line-strong bg-surface px-4 text-sm font-medium text-fg hover:bg-raised"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Edit an open term — PATCH /api/renewals/:id (only changed fields are sent)
// ---------------------------------------------------------------------------------------------

const editSchema = z
  .object({
    termStart: dateField('term start'),
    renewalDate: dateField('renewal date'),
    price: priceField,
  })
  .refine(
    (v) =>
      !isValidIsoDate(v.termStart) || !isValidIsoDate(v.renewalDate) || v.termStart < v.renewalDate,
    {
      path: ['renewalDate'],
      message: ORDER_MESSAGE,
    },
  );
type EditFields = z.infer<typeof editSchema>;

const editValues = (r: Renewal): EditFields => ({
  termStart: r.termStart,
  renewalDate: r.renewalDate,
  price: r.price.amount,
});

export function EditRenewalForm({ renewal }: { renewal: Renewal }) {
  const update = useUpdateRenewal(renewal.id);
  const [saved, setSaved] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditFields>({
    resolver: zodResolver(editSchema),
    defaultValues: editValues(renewal),
  });

  // Show the API's values when a new version of the term arrives, without re-mounting the form
  // (so the save confirmation stays visible).
  const version = renewal.updatedAt;
  useEffect(() => {
    reset(editValues(renewal));
  }, [version]);

  const onSubmit = handleSubmit((v) => {
    setSaved(false);
    const current = editValues(renewal);
    const patch = Object.fromEntries(
      (Object.keys(v) as (keyof EditFields)[])
        .filter((k) => v[k] !== current[k])
        .map((k) => [k, v[k]]),
    );
    if (Object.keys(patch).length === 0) {
      setSaved(true);
      return;
    }
    update.mutate(patch, { onSuccess: () => setSaved(true) });
  });

  return (
    <form onSubmit={onSubmit} noValidate aria-label="Edit renewal term" className="space-y-4">
      <Field
        id="edit-term-start"
        label="Term start"
        error={errors.termStart?.message}
        inputProps={{ ...register('termStart'), type: 'date' }}
      />
      <Field
        id="edit-renewal-date"
        label="Renewal date"
        error={errors.renewalDate?.message}
        inputProps={{ ...register('renewalDate'), type: 'date' }}
      />
      <Field
        id="edit-price"
        label="Price"
        hint={PRICE_HINT}
        error={errors.price?.message}
        inputProps={{ ...register('price'), inputMode: 'decimal', autoComplete: 'off' }}
      />
      {update.isError ? (
        <p role="alert" className="text-sm text-danger">
          {update.error.message}
        </p>
      ) : null}
      {saved ? (
        <p role="status" className="text-sm text-success">
          The renewal term was saved.
        </p>
      ) : null}
      <Button type="submit" disabled={update.isPending}>
        {update.isPending ? 'Saving…' : 'Save changes'}
      </Button>
    </form>
  );
}

// ---------------------------------------------------------------------------------------------
// Renew — POST /api/renewals/:id/renew (closes this term, opens the next)
// ---------------------------------------------------------------------------------------------

export function RenewForm({ renewal }: { renewal: Renewal }) {
  const renew = useRenewTerm(renewal.id);
  const navigate = useNavigate();
  const renewSchema = z
    .object({
      termStart: z.string().refine((v) => v === '' || isValidIsoDate(v), {
        message: 'Enter the term start as a valid date, or leave it empty.',
      }),
      renewalDate: dateField('new renewal date'),
      price: priceField,
    })
    .refine((v) => !isValidIsoDate(v.renewalDate) || v.renewalDate > renewal.renewalDate, {
      path: ['renewalDate'],
      message: `The new renewal date must be after ${formatDate(renewal.renewalDate)}.`,
    })
    .refine(
      (v) => v.termStart === '' || !isValidIsoDate(v.renewalDate) || v.termStart < v.renewalDate,
      { path: ['renewalDate'], message: ORDER_MESSAGE },
    );
  type RenewFields = z.infer<typeof renewSchema>;
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RenewFields>({
    resolver: zodResolver(renewSchema),
    defaultValues: { termStart: '', renewalDate: '', price: '' },
  });

  const onSubmit = handleSubmit((v) =>
    renew.mutate(
      {
        renewalDate: v.renewalDate,
        price: v.price,
        ...(v.termStart ? { termStart: v.termStart } : {}),
      },
      {
        onSuccess: (result) =>
          void navigate(`/app/renewals/${result.opened.id}`, {
            state: {
              notice: 'The term was renewed: the previous term is closed and this one is open.',
            },
          }),
      },
    ),
  );

  return (
    <form onSubmit={onSubmit} noValidate aria-label="Renew" className="space-y-4">
      <Field
        id="renew-term-start"
        label="New term start (optional)"
        hint={`Leave empty to start the new term on the current renewal date (${formatDate(renewal.renewalDate)}).`}
        error={errors.termStart?.message}
        inputProps={{ ...register('termStart'), type: 'date' }}
      />
      <Field
        id="renew-renewal-date"
        label="New renewal date"
        error={errors.renewalDate?.message}
        inputProps={{ ...register('renewalDate'), type: 'date' }}
      />
      <Field
        id="renew-price"
        label="Price for the new term"
        hint={`${PRICE_HINT} The price is never carried over.`}
        error={errors.price?.message}
        inputProps={{ ...register('price'), inputMode: 'decimal', autoComplete: 'off' }}
      />
      {renew.isError ? (
        <p role="alert" className="text-sm text-danger">
          {renew.error.message}
        </p>
      ) : null}
      <Button type="submit" disabled={renew.isPending}>
        {renew.isPending ? 'Renewing…' : 'Renew'}
      </Button>
    </form>
  );
}
